import type { AnswerType, CostType, Currency, UserPlan, UserRole } from '@/lib/types'

/**
 * The account no client may demote. Mirrors `isSuperAdmin()` in ../firestore_prod.rules — the rule
 * is the real protection; this constant only lets the UI explain why the buttons are disabled.
 */
export const SUPER_ADMIN_EMAIL = 'akhundof@gmail.com'

export function isSuperAdminEmail(email: string | null | undefined): boolean {
  return (email ?? '').trim().toLowerCase() === SUPER_ADMIN_EMAIL
}

/** Same sets the security rules validate against. */
export const USER_ROLES: readonly UserRole[] = ['admin', 'user']
export const USER_PLANS: readonly UserPlan[] = ['bronze', 'silver', 'gold', 'platinum']

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Администратор',
  user: 'Пользователь',
}

export const PLAN_LABELS: Record<UserPlan, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
}

/** Catalog line kinds, same names as the `CostType` enum of the Android app. */
export const COST_TYPES: readonly CostType[] = ['MATERIAL', 'LABOR', 'EQUIPMENT', 'LOGISTICS']

export const COST_TYPE_LABELS: Record<CostType, string> = {
  MATERIAL: 'Материал',
  LABOR: 'Работа',
  EQUIPMENT: 'Оборудование',
  LOGISTICS: 'Логистика',
}

/**
 * Catalog content is authored in Russian (the base value); these are the languages that can carry
 * an override, keyed by the same tags the app uses.
 */
export const TRANSLATION_LANGUAGES: ReadonlyArray<{ tag: string; label: string }> = [
  { tag: 'en', label: 'English' },
  { tag: 'az', label: 'Azərbaycan' },
]

/** Currencies of the app (`SupportedCurrency`); AZN is the default. */
export const CURRENCIES: readonly Currency[] = ['AZN', 'USD']

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  AZN: '₼',
  USD: '$',
}

/** Answer types of a configured question (`ConfigAnswerType`), in the app's own order. */
export const ANSWER_TYPES: readonly AnswerType[] = [
  'NUMBER',
  'BOOLEAN',
  'CHOICE',
  'MULTI_CHOICE',
  'TEXT',
  'SELECT',
  'KEY_SET',
  'REPEATER',
  'QUESTION_LIST',
  'FACT',
]

export const ANSWER_TYPE_LABELS: Record<AnswerType, string> = {
  NUMBER: 'Число',
  BOOLEAN: 'Да/Нет',
  CHOICE: 'Выбор',
  MULTI_CHOICE: 'Выбор несколько',
  TEXT: 'Текст',
  SELECT: 'Выпадающий список',
  KEY_SET: 'Набор ключей (да/нет)',
  REPEATER: 'Повторитель (список вопросов)',
  QUESTION_LIST: 'Список вопросов',
  FACT: 'Факт',
}

/** Short hints shown in the question editor next to the answer type. */
export const ANSWER_TYPE_NOTES: Record<AnswerType, string> = {
  NUMBER: 'Ответ — число. Эффекты используют его в формуле через {ключ}.',
  BOOLEAN: 'Да/нет. Необязательный вопрос: не влияет на счётчик прогресса.',
  CHOICE: 'Пользователь выбирает ОДИН материал из категории. Эффект применяется к выбранному материалу.',
  MULTI_CHOICE:
    'Пользователь выбирает НЕСКОЛЬКО материалов из категории и количество для каждого. Каждый эффект применяется к каждому выбранному материалу: результат × количество.',
  TEXT: 'Свободный текст. В формулах почти не используется.',
  SELECT: 'Варианты списка. В факт пишется value варианта — на него ссылаются условия.',
  KEY_SET: 'Каждый элемент — свой факт да/нет: value элемента и есть ключ факта.',
  REPEATER: 'Поля схемы задают, из чего состоит одна запись; эффекты выполняются для каждой записи.',
  QUESTION_LIST:
    'Те же поля, что у повторителя, но пользователь заполняет их один раз (без добавления записей); эффекты выполняются один раз.',
  FACT:
    'Не вопрос: пользователь его не видит, в прогрессе он не учитывается, ключа факта нет. Эффекты добавляются в каждом помещении, где применяется вопрос (например, постоянный пункт работы для группы «Сантехника»). Условие = «применять только когда». Чтобы не учитывать стоимость, пользователь ставит цену пункта 0 в смете.',
}

/** Answer types usable as a field type of a REPEATER / QUESTION_LIST schema. */
export const FIELD_TYPES: readonly AnswerType[] = ['NUMBER', 'TEXT', 'BOOLEAN', 'CHOICE']

/**
 * Which answer types own documents in `ruleQuestionOptions`, and what those options mean there.
 *
 * MULTI_CHOICE is deliberately absent: its "variants" are the materials of its category, picked
 * during the interview, so it stores no options at all (see `QuestionEditScreen.kt`, where
 * MULTI_CHOICE falls through to the effects-only branch).
 */
export const OPTION_KIND: Partial<Record<AnswerType, string>> = {
  SELECT: 'Варианты списка',
  KEY_SET: 'Элементы набора (value = ключ факта)',
  REPEATER: 'Поля схемы (value = ключ поля)',
  QUESTION_LIST: 'Поля схемы (value = ключ поля)',
}

/** Answer types whose question owns a material category (`materialCategory`). */
export const CATEGORY_ANSWER_TYPES: readonly AnswerType[] = ['CHOICE', 'MULTI_CHOICE']

/**
 * Answer types for which the question-level unit is meaningless: the answer is a material (or a set
 * of fields), not a measured amount. Mirrors the app, which hides the unit field for them.
 */
export const UNITLESS_ANSWER_TYPES: readonly AnswerType[] = [
  'CHOICE',
  'MULTI_CHOICE',
  'SELECT',
  'KEY_SET',
  'REPEATER',
  'QUESTION_LIST',
  'FACT',
]
